import axiosInstance from '../../api/api';
import { API_ROUTES } from '../../config/api-routes';
import { isNodeBackend } from '../../utils/backendConfig';

export type ActivityEvent = {
  id: string;
  category: string;
  actor_type: 'user' | 'recruiter' | 'system';
  message: string;
  cta_label: string;
  cta_path: string;
  metadata?: Record<string, unknown>;
  created_at: string;
};

export type ActivityFeedResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: ActivityEvent[];
};

export type RecruiterTrendCard = {
  id: string;
  title: string;
  trend_summary: string;
  action_text: string;
  source_name: string;
  source_url: string;
  cta_type: string;
  cta_target: string;
  cta_label?: string;
  published_at: string;
};

function actorTypeForCategory(category: string): ActivityEvent['actor_type'] {
  if (category.startsWith('recruiter')) return 'recruiter';
  if (category.startsWith('user')) return 'user';
  return 'system';
}

async function fetchActivityFeedNode(
  page: number,
  pageSize: number
): Promise<ActivityFeedResponse> {
  const offset = Math.max(0, (page - 1) * pageSize);
  const { data } = await axiosInstance.get<{
    success?: boolean;
    items?: Array<{
      id: string;
      message: string;
      timestamp: string;
      category: string;
      ctaLabel?: string | null;
      ctaHref?: string | null;
    }>;
  }>(API_ROUTES.DASHBOARD.ACTIVITIES, {
    params: { limit: pageSize, offset },
  });

  const items = Array.isArray(data?.items) ? data.items : [];
  const results: ActivityEvent[] = items.map((row) => ({
    id: String(row.id),
    category: row.category || 'system',
    actor_type: actorTypeForCategory(row.category || ''),
    message: row.message || '',
    cta_label: row.ctaLabel || '',
    cta_path: row.ctaHref || '',
    created_at: row.timestamp || new Date().toISOString(),
  }));

  return {
    count: results.length,
    next: results.length >= pageSize ? `page=${page + 1}` : null,
    previous: page > 1 ? `page=${page - 1}` : null,
    results,
  };
}

async function fetchActivityFeedDjango(
  page: number,
  pageSize: number
): Promise<ActivityFeedResponse> {
  const { data } = await axiosInstance.get<ActivityFeedResponse>(API_ROUTES.ACTIVITY.FEED, {
    params: { page, page_size: pageSize },
  });
  return {
    count: data.count ?? 0,
    next: data.next ?? null,
    previous: data.previous ?? null,
    results: Array.isArray(data.results) ? data.results : [],
  };
}

export async function fetchActivityFeed(page = 1, pageSize = 20): Promise<ActivityFeedResponse> {
  if (isNodeBackend()) {
    return fetchActivityFeedNode(page, pageSize);
  }
  return fetchActivityFeedDjango(page, pageSize);
}

export async function fetchExternalActivityFeed(
  page = 1,
  pageSize = 20
): Promise<ActivityFeedResponse> {
  if (isNodeBackend()) {
    const feed = await fetchActivityFeedNode(page, pageSize);
    return {
      ...feed,
      results: feed.results.filter((row) => row.category.startsWith('recruiter')),
    };
  }
  const { data } = await axiosInstance.get<ActivityFeedResponse>(API_ROUTES.ACTIVITY.FEED, {
    params: { page, page_size: pageSize, feed: 'external' },
  });
  return {
    count: data.count ?? 0,
    next: data.next ?? null,
    previous: data.previous ?? null,
    results: Array.isArray(data.results) ? data.results : [],
  };
}

function mapNodeTrend(row: {
  id: string;
  title: string;
  summary?: string;
  actionText?: string;
  sourceLabel?: string;
  sourceUrl?: string;
}): RecruiterTrendCard {
  return {
    id: row.id,
    title: row.title,
    trend_summary: row.summary || '',
    action_text: row.actionText || '',
    source_name: row.sourceLabel || '',
    source_url: row.sourceUrl || '',
    cta_type: 'external',
    cta_target: row.sourceUrl || '',
    cta_label: 'Read more',
    published_at: new Date().toISOString(),
  };
}

async function fetchCurrentTrendsNode(): Promise<RecruiterTrendCard[]> {
  const { data } = await axiosInstance.get<{
    success?: boolean;
    trends?: Array<{
      id: string;
      title: string;
      summary?: string;
      actionText?: string;
      sourceLabel?: string;
      sourceUrl?: string;
    }>;
  }>(API_ROUTES.DASHBOARD.TRENDS);
  const rows = Array.isArray(data?.trends) ? data.trends : [];
  return rows.map(mapNodeTrend);
}

export async function fetchCurrentTrends(): Promise<RecruiterTrendCard[]> {
  if (isNodeBackend()) {
    return fetchCurrentTrendsNode();
  }
  const { data } = await axiosInstance.get<RecruiterTrendCard[]>(API_ROUTES.TRENDS.CURRENT);
  return Array.isArray(data) ? data : [];
}

export async function dismissTrend(trendId: string): Promise<void> {
  if (isNodeBackend()) {
    return;
  }
  await axiosInstance.post(API_ROUTES.TRENDS.dismiss(trendId), {});
}

export async function trendCtaClick(trendId: string): Promise<void> {
  if (isNodeBackend()) {
    return;
  }
  await axiosInstance.post(API_ROUTES.TRENDS.ctaClick(trendId), {});
}

export async function trendSourceClick(trendId: string): Promise<void> {
  if (isNodeBackend()) {
    return;
  }
  await axiosInstance.post(API_ROUTES.TRENDS.sourceClick(trendId), {});
}
